/* eslint-disable max-lines */
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams
} from 'react-router-dom';
import React, { Suspense, lazy, useCallback, useEffect } from 'react';
import { SELECTED_COMPANY_ID, pageTitles } from '../constants';
import { useDispatch, useSelector } from 'react-redux';
import { getUserCompanies, getUserProfileData } from 'ducks/user/actions';
import {
  useFeatureFlags,
  useFeatureFlagsAnyEnabled
} from '@hooks/useFeatureFlags';
import useLocationChange, {
  LocationSnapshotType
} from '_clean-code/hooks/useLocationChange';

import AppLayout from '../layouts/AppLayout';
import { MachineUIConfigScreen } from '_clean-code/pages/UIConfigScreen/MachineUIConfigScreen';
import ReactGA from 'react-ga4';
import Spinner from '@components/spinner';
import { SpinnerComponent } from '@components/index';
import { Translate } from '@utils/i18n';
import { fetchSettingsForProduction } from '_clean-code/ducks/production/actions';
import { getCompanyUIConfig } from 'ducks/config/actions';
import { getMachines } from 'ducks/machines/actions';
import { getTimeDiffInSeconds } from '@utils/dates';
import { loadMachinesSortingFilter } from '_clean-code/ducks/realtime/actions';
import { sendEventTrack } from '@utils/analytics';
import styles from './mainLayoutRoute.module.scss';
import { useUserCompanies } from '_clean-code/api/hooks/useUserCompanies';
import { useUserProfile } from '_clean-code/api/hooks/useUserProfile';
import { EMachineListsVariants } from '_clean-code/ducks/realtime/reducers';

const LazyHistorical = lazy(() => import('../containers/historical'));
const LazyStops = lazy(() => import('../_clean-code/pages/StopsPage'));
const LazyProduction = lazy(
  () => import('../_clean-code/pages/ProductionPage')
);
const LazyBasicProduction = lazy(
  () => import('../_clean-code/pages/BasicProductionPage')
);
const LazyMetabaseDashboard = lazy(
  () => import('../_clean-code/pages/Metabase')
);
const LazyShiftsNotConsidered = lazy(
  () => import('../containers/shifts-not-considered')
);
const LazyAlertsSettingsV3 = lazy(
  () => import('../containers/settings/alerts-new')
);
const LazyStopSettings = lazy(() => import('../containers/settings/stops'));
const LazyEquipmentSettings = lazy(
  () => import('../containers/settings/equipments')
);
const LazyTicket = lazy(() => import('../containers/ticket'));
const LazyAppSettings = lazy(() => import('../containers/app-settings'));
const LazyDataReports = lazy(() => import('../containers/data'));
const MainDashboardPage = lazy(() => import('../pages/mainDashboardPage'));
const LazyStopAutomation = lazy(() => import('../containers/stop-automation'));
const LazyMachinesSetting = lazy(
  () => import('../containers/machine-settings')
);
const RealTimePage = lazy(() => import('../_clean-code/pages/RealTimePage'));
const ShiftSettingsPage = lazy(
  () => import('../_clean-code/pages/ShiftSettingsPage')
);
const DataAnalysisPage = lazy(
  () => import('../_clean-code/pages/DataAnalysisPage')
);
const UIConfigScreen = lazy(
  () => import('../_clean-code/pages/UIConfigScreen')
);
const LazyProductionSettings = lazy(
  () => import('../_clean-code/pages/ProductionSettings')
);

const Reports = lazy(() => import('../_clean-code/pages/Reports'));

const LazySettingsV2 = lazy(() => import('../_clean-code/pages/SettingsV2'));
const LazyRealtimeSettings = lazy(
  () => import('../_clean-code/pages/RealtimeSettings')
);

function MainLayoutRoute() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { data: companies } = useUserCompanies();
  const { userData } = useUserProfile({ enabled: !!companies });
  const { reportSubscription } = useFeatureFlags();
  const { availability, prodBasic, prodFull, prodLegacy } =
    useFeatureFlagsAnyEnabled();

  const onLocationChange = useCallback(
    (
      previousSnapshot: LocationSnapshotType,
      newSnapshot: LocationSnapshotType | null
    ) => {
      if (
        previousSnapshot.url !== '/equipmentsettings' &&
        previousSnapshot.url !== '/stopsettings' &&
        previousSnapshot.url !== '/production-settings'
      ) {
        const eventData = {
          url: previousSnapshot.url,
          duration: getTimeDiffInSeconds(previousSnapshot.time, new Date()),
          companyName: userData?.companies?.[0]?.name ?? 'unknown'
        };
        if (newSnapshot) {
          ReactGA.send({
            hitType: 'pageview',
            page: newSnapshot.url,
            title: pageTitles[newSnapshot.url],
            companyId: Number(localStorage.getItem(SELECTED_COMPANY_ID)),
            companyName: userData?.companies?.[0]?.name ?? 'unknown'
          });
        }
        sendEventTrack('Visit Page', eventData);
      }
    },
    [userData]
  );
  useLocationChange(onLocationChange);

  useEffect(() => {
    const loadData = () => {
      dispatch(getCompanyUIConfig());
      dispatch(fetchSettingsForProduction());
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accessToPulsarUsers = () => {
    const isSuperAdmin =
      userData?.role === 'superuser' || userData?.role === 'superadmin';
    if (isSuperAdmin) {
      if (
        userData?.email?.includes('@pulsarml.com') &&
        userData?.companyId === 168
      )
        return true;
    }

    return false;
  };

  return (
    <Routes>
      <Route
        path="dashboard"
        element={
          <WithMainLayout
            title={<Translate value="dashboard" />}
            render={() => <MainDashboardPage />}
          />
        }
      />
      <Route
        path="analytics/*"
        element={
          <WithMainLayout
            title={<Translate value="analytics_sidebar" />}
            className={styles.pareto}
            render={(params) => (
              <LazyHistorical {...({ match: { params } } as any)} />
            )}
          />
        }
      />
      <Route
        path="machines"
        element={
          <WithMainLayout
            title={<Translate value="machines_sidebar" />}
            render={() => <RealTimePage />}
          />
        }
      />
      <Route
        path="machines/tv-mode"
        element={
          <WithMainLayout
            title={<Translate value="machines_sidebar" />}
            render={() => <RealTimePage isTvLayoutRoute />}
          />
        }
      />
      <Route
        path="machines/:machineId/ui-configure"
        element={
          <WithMainLayout
            title={<Translate value="ui_config" />}
            render={({ machineId }) => (
              <MachineUIConfigScreen machineId={machineId} />
            )}
          />
        }
      />
      <Route
        path="reports/*"
        element={
          <WithMainLayout
            title={<Translate value="reports" />}
            render={() => (reportSubscription ? <Reports /> : GoToDashboard)}
          />
        }
      />
      <Route
        path="production/*"
        element={
          <WithMainLayout
            //TODO: add subtitle
            title={<Translate value="production_sidebar" />}
            render={(params) =>
              prodLegacy ? (
                <LazyProduction {...({ match: { params } } as any)} />
              ) : prodBasic || prodFull ? (
                <LazyBasicProduction {...({ match: { params } } as any)} />
              ) : (
                GoToDashboard
              )
            }
          />
        }
      />
      <Route
        path="alerts/*"
        element={
          <WithMainLayout
            title={<Translate value="alert_settings" />}
            className={styles.alerts}
            render={(params) =>
              availability ? (
                <LazyAlertsSettingsV3
                  {...({ match: { params } } as any)}
                  key={location.key}
                />
              ) : (
                GoToDashboard
              )
            }
          />
        }
      />
      <Route
        path="stopsettings/*"
        element={
          <WithMainLayout
            title={<Translate value="stop_label_settings" />}
            className={styles.stopLabels}
            render={(params) =>
              availability ? (
                <LazyStopSettings {...({ match: { params } } as any)} />
              ) : (
                GoToDashboard
              )
            }
          />
        }
      />
      <Route
        path="equipmentsettings/*"
        element={
          <WithMainLayout
            title={<Translate value="equipment_settings" />}
            className={styles.equipment}
            render={(params) =>
              availability ? (
                <LazyEquipmentSettings {...({ match: { params } } as any)} />
              ) : (
                GoToDashboard
              )
            }
          />
        }
      />
      <Route
        path="stopautomation"
        element={
          <WithMainLayout
            title={<Translate value="stop_automation" />}
            render={(params) =>
              availability ? (
                <LazyStopAutomation {...{ match: { params } }} />
              ) : (
                GoToDashboard
              )
            }
          />
        }
      />
      <Route
        path="stops"
        element={
          <WithMainLayout
            title={<Translate value="stops" />}
            render={(params) =>
              availability ? (
                <LazyStops {...({ match: { params } } as any)} />
              ) : (
                GoToDashboard
              )
            }
          />
        }
      />
      <Route
        path="shift-not-considered"
        element={
          <WithMainLayout
            title={<Translate value="idleShiftsSettings" />}
            className={styles.idleShifts}
            render={(params) =>
              availability ? (
                <LazyShiftsNotConsidered {...{ match: { params } }} />
              ) : (
                GoToDashboard
              )
            }
          />
        }
      />
      <Route
        path="create-ticket"
        element={
          <WithMainLayout
            title={<Translate value="support_ticket" />}
            className={styles.tickets}
            render={(params) => <LazyTicket {...{ match: { params } }} />}
          />
        }
      />
      <Route
        path="machine-settings"
        element={
          <WithMainLayout
            title={<Translate value="machine_settings" />}
            render={(params) => (
              <LazyMachinesSetting {...({ match: { params } } as any)} />
            )}
          />
        }
      />
      <Route
        path="users-settings"
        element={
          <WithMainLayout
            title={<Translate value="users_settings" />}
            render={(params) => <LazyAppSettings {...{ match: { params } }} />}
          />
        }
      />
      <Route
        path="data/reports"
        element={
          <WithMainLayout
            title={<Translate value="data_reports" />}
            render={(params) => <LazyDataReports {...{ match: { params } }} />}
          />
        }
      />
      <Route
        path="shift-settings"
        element={
          <WithMainLayout
            title={<Translate value="shift_schedule" />}
            render={(params) => (
              <ShiftSettingsPage {...({ match: { params } } as any)} />
            )}
          />
        }
      />
      {accessToPulsarUsers() && (
        <Route
          path="my-dashboards"
          element={
            <WithMainLayout
              title={<Translate value="pulsar_analytics_dashboard" />}
              render={(params) => (
                <LazyMetabaseDashboard {...({ match: { params } } as any)} />
              )}
            />
          }
        />
      )}
      <Route
        path="data-analysis/*"
        element={
          <WithMainLayout
            title={<Translate value="data-analysis" />}
            render={(params) => (
              <DataAnalysisPage {...({ match: { params } } as any)} />
            )}
          />
        }
      />
      <Route
        path="ui-config/*"
        element={
          <WithMainLayout
            title={<Translate value="ui_config" />}
            render={(params) => (
              <UIConfigScreen
                {...({ match: { params } } as any)}
                key={location.key}
              />
            )}
          />
        }
      />
      <Route
        path="production-settings/*"
        element={
          <WithMainLayout
            title={<Translate value="production_settings" />}
            render={(params) => (
              <LazyProductionSettings
                {...({ match: { params } } as any)}
                key={location.key}
              />
            )}
          />
        }
      />
      {import.meta.env.DEV && (
        <Route
          path="settings-v2/*"
          element={
            <WithMainLayout
              title={<Translate value="settings" />}
              render={(params) => (
                <LazySettingsV2 {...({ match: { params } } as any)} />
              )}
            />
          }
        />
      )}
      {import.meta.env.DEV && (
        <Route
          path="realtime-settings/*"
          element={
            <WithMainLayout
              title={<Translate value="realtime" />}
              render={(params) => (
                <LazyRealtimeSettings {...({ match: { params } } as any)} />
              )}
            />
          }
        />
      )}
      <Route path="*" element={GoToDashboard} />
    </Routes>
  );
}

export default MainLayoutRoute;

const WithMainLayout = (props: {
  render: (params: any) => React.ReactElement;
  title?: any;
  className?: string;
}) => {
  const params = useParams();

  const {
    data: companies,
    selectedCompany,
    isLoading: isCompaniesLoading
  } = useUserCompanies();
  const userProfile = useUserProfile({ enabled: !!companies });
  const machinesStore = useSelector((store: any) => store.machines);
  const dispatch = useDispatch();

  useEffect(() => {
    // after we have the user company in storage, we can dispatch these calls, as the company id is embedded in each
    // request header
    if (selectedCompany) {
      // even though we are consuming the user profile and companies from the hooks, we need to dispatch these
      // as they include important side effects and other components consume state from the store
      dispatch(getUserCompanies());
      dispatch(getUserProfileData());
      dispatch(getMachines({}));
      dispatch(
        loadMachinesSortingFilter(
          String(selectedCompany.companyId),
          EMachineListsVariants.Default
        )
      );
      dispatch(
        loadMachinesSortingFilter(
          String(selectedCompany.companyId),
          EMachineListsVariants.Shift
        )
      );
    }
  }, [dispatch, selectedCompany]);

  if (
    userProfile === undefined ||
    userProfile.isLoading ||
    isCompaniesLoading ||
    machinesStore.isMachinesListLoading
  ) {
    return (
      <div>
        <Spinner id="main-loader" />
      </div>
    );
  }

  return (
    <AppLayout title={props.title} mainContentClassName={props.className}>
      <Suspense fallback={<SpinnerComponent id="fallback-loader" />}>
        {props.render(params)}
      </Suspense>
    </AppLayout>
  );
};

const GoToDashboard = <Navigate to="/dashboard" />;
